<template>

    <div id="builder-canvas">
        <v-emoji-selector :toggle="emojiToggle" :target="emojiTarget" @close="emojiToggle = null" />
        <v-var-selector :toggle="varToggle" :target="varTarget" @close="varToggle = null" />
        <v-builder-formatter />
        <v-builder-tooltip />
        <div id="builder-html">
            <div v-html="html"></div>
        </div>
    </div>

</template>

<style lang="scss">

    #builder-html {
        #template .template-body {
            padding: 20px;
        }
        .block-wrapper {
            &.active {
                border: 2px solid $color-primary;
                z-index: 2;
                .block-toolbar {
                    display: flex;
                }
            }
            &.disabled,
            &.sortable-chosen {
                box-shadow: none;
                user-select: none;
            }
            .block-toolbar {
                display: none;
            }
            .block-item:not([contenteditable]) {
                pointer-events: none;
            }
        }
        .block-toolbar {
            @include font-semibold(10px);
            align-items: center;
            background: $color-primary;
            color: $color-text;
            display: flex;
            justify-content: space-between;
            padding: 5px;
            user-select: none;
            .actions {
                display: flex;
                .action {
                    @include font-bold(8px);
                    appearance: none;
                    background: none;
                    border-radius: 3px;
                    color: #fff;
                    padding: 5px 7px;
                    text-decoration: none;
                    text-transform: uppercase;
                    transition: background-color .3s;
                    &:first-child {
                        margin-left: 0;
                    }
                    &:hover {
                        background-color: rgba(255,255,255,0.2);
                    }
                }
            }
        }
        .sortable-ghost {
            opacity: 0;
        }
    }

</style>

<style scoped lang="scss">

    @include scrollbar();

    #builder-canvas {
        background: #fff;
        box-sizing: border-box;
        display: flex;
        flex-grow: 1;
        overflow-x: none;
        overflow-y: auto;
        position: relative;
    }

    #builder-html {
        flex-grow: 1;
        > div {
            height: 100%;
            width: 100%;
        }
    }

</style>

<script>

import BuilderFormatter from '@builder/BuilderFormatter';
import BuilderTooltip from '@builder/BuilderTooltip';
import BuilderUtil from './utils/BuilderUtil';
import EmojiSelector from '@cmp/EmojiSelector';
import sanitizeHtml from 'sanitize-html';
import Sortable from 'sortablejs';

export default {

    components: {
        'v-builder-formatter': BuilderFormatter,
        'v-builder-tooltip': BuilderTooltip,
        'v-emoji-selector': EmojiSelector,
    },

    mounted() {
        // Add listeners.
        document.getElementById('builder-html').addEventListener('mousedown', this.onMouseDown);
        document.getElementById('builder-html').addEventListener('paste', this.onPaste);
        document.getElementById('builder-html').addEventListener('click', this.onClick);

        // Init blocks.
        document.querySelectorAll('.block').forEach(el => {
            this.initBlockElement(el);
        });
        this.sortBlockElements();
    },

    data() {
        return {
            emojiToggle: null,
            emojiTarget: null,
            varToggle: null,
            varTarget: null,
        }
    },

    watch: {

        html() {
            this.$nextTick(() => {
                document.querySelectorAll('.block').forEach(el => {
                    this.initBlockElement(el);
                });
                this.sortBlockElements();
            });
        },

    },

    computed: {

        mode() {
            return this.$store.getters['builder/mode'];
        },

        html() {
            return this.$store.getters['builder/html'];
        },

        blocks() {
            return this.$store.getters['builder/blocks'];
        },

        library() {
            return this.$store.getters['builder/library'];
        },

        libraryCustom() {
            return this.$store.getters['builder/libraryCustom'];
        },

    },

    methods: {

        onMouseDown(e) {
            // Select block.
            let blockElement = e.target.closest('.block');
            if (blockElement) {
                let index = BuilderUtil.getBlockElementIndex(blockElement);
                let type = blockElement.getAttribute('data-type');
                console.log(`Selected block at index: ${index} of type ${type}`);
                this.selectBlockElement(type, blockElement, index);
            } else {
                this.$store.commit('builder/setSelectedPanel', null);
            }
        },

        onPaste(e) {
            e.preventDefault();
            let text = (e.originalEvent || e).clipboardData.getData('text/html');
            if (!text) text = (e.originalEvent || e).clipboardData.getData('text');
            let clean = sanitizeHtml(text, {
                allowedTags: ['a', 'b', 'br', 'em', 'font', 'i', 'p', 'strong', 'u'],
                allowedAttributes: {
                    '*': ['color'],
                    'a': ['href', 'style'],
                },
                allowedStyles: {
                    '*': { 'color': [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/] },
                },
            });
            document.execCommand('insertHTML', false, clean.trim());
        },

        onClick(e) {
            if (e.target?.classList.contains('action-drop')) {
                this.$store.commit('builder/setSelectedModal', 'remove');
            } else if (e.target?.classList.contains('action-apply')) {
                this.$store.commit('builder/setSelectedModal', 'apply');
            } else if (e.target?.classList.contains('action-copy')) {
                this.$store.commit('builder/setSelectedModal', 'copy');
            } else if (e.target?.classList.contains('action-add')) {
                this.$store.commit('builder/setSelectedModal', 'add');
            } else if (e.target?.classList.contains('action-emoji')) {
                this.onEmoji(e);
            } else if (e.target?.classList.contains('action-traits')) {
                this.onTraits(e);
            }
        },

        /**
         * Block actions
         */

        sortBlockElements() {
            Sortable.create(document.getElementById('blocks'), {
                animation: 100,
                direction: 'vertical',
                handle: '.action-move',
                group: {
                    name: 'blocks',
                    pull: false,
                    put: ['library', 'library-custom'],
                },
                onStart: (e) => {
                    BuilderUtil.disableBlockElements(e.item);
                },
                onEnd: (e) => {
                    let blocks = Object.assign([], this.blocks);
                    let block = Object.assign({}, blocks[e.oldIndex]);
                    blocks.splice(e.oldIndex, 1);
                    blocks.splice(e.newIndex, 0, block);
                    this.$store.commit('builder/setBlocks', blocks);
                    this.$store.commit('builder/setSelectedBlockIndex', e.newIndex);
                    BuilderUtil.enableBlockElements();
                },
                onAdd: (e) => {
                    let blocks = Object.assign([], this.blocks);
                    let item = null;
                    if (e.item.className.includes('block-item-custom')) {
                        item = Object.assign({}, this.libraryCustom[e.oldIndex]);
                    } else {
                        item = Object.assign({}, this.library[e.oldIndex]);
                    }
                    blocks.splice(e.newIndex, 0, item.block);
                    this.$store.commit('builder/setBlocks', blocks);
                    this.$store.commit('builder/setSelectedBlockIndex', e.newIndex);
                    this.blockItemAdded(item, e.newIndex);
                    e.item.replaceWith('');
                },
            });
        },

        blockItemAdded(item, index) {
            let parent = document.getElementById('blocks');
            let el = document.createElement('div');
            el.innerHTML = item.html.trim();
            parent.insertBefore(el.firstChild, parent.children[index]);
            this.$nextTick(() => {
                let blockElement = BuilderUtil.getBlockElementAtIndex(index);
                this.initBlockElement(blockElement);
                this.sortBlockElements();
                this.selectBlockElement(item.block.type, blockElement, index);
            });
        },

        initBlockElement(blockElement) {
            // Is block editable?
            if (['headline', 'paragraph'].includes(blockElement.getAttribute('data-type'))) {
                blockElement.querySelectorAll('.block-item')[0]
                    .setAttribute('contenteditable', true);
            }
            // Add toolbar.
            let el = document.createElement('div');
            el.className = 'block-toolbar';
            let html = '';
            html += '<div class="actions">';
            html += '<button class="action action-move"><i class="fas fa-arrows-alt mr-2"></i>Move</button>';
            html += '<button class="action action-copy">Copy</button>';
            html += '<button class="action action-apply">Apply</button>';
            html += '<button class="action action-add">Save</button>';
            html += '<button class="action action-drop">Remove</button>';
            html += '</div>';
            if (['headline', 'paragraph'].includes(blockElement.getAttribute('data-type'))) {
                html += '<div class="actions">';
                html += '<button class="action action-emoji">Emoji</button>';
                html += '<button class="action action-traits">Variable</button>';
                html += '</div>';
            }
            el.innerHTML = html;
            blockElement.prepend(el);
        },

        selectBlockElement(panel, el, index) {
            if (el.parentElement.classList.contains('active')) return;
            this.$store.commit('builder/setSelectedBlockIndex', index);
            BuilderUtil.activateBlockElement(el);
        },

        /**
         * Block toolbar actions
         */

        onEmoji(e) {
            if (!this.emojiToggle) {
                this.emojiToggle = e.target;
                let selectedBlockIndex = this.$store.getters['builder/selectedBlockIndex'];
                let selectedBlockElement = BuilderUtil.getBlockElementAtIndex(selectedBlockIndex);
                this.emojiTarget = selectedBlockElement.getElementsByClassName('block-item')[0];
            } else {
                this.emojiToggle = null;
            }
        },

        onTraits(e) {
            if (!this.varToggle) {
                this.varToggle = e.target;
                let selectedBlockIndex = this.$store.getters['builder/selectedBlockIndex'];
                let selectedBlockElement = BuilderUtil.getBlockElementAtIndex(selectedBlockIndex);
                this.varTarget = selectedBlockElement.getElementsByClassName('block-item')[0];
            } else {
                this.varToggle = null;
            }
        },

    },

}

</script>